import React from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import './PuppyCard.css'

const PuppyCard = ({ puppy, onClick, index }) => {
  const navigate = useNavigate()

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        delay: index * 0.1,
        ease: "easeOut"
      }
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'status-available'
      case 'Reserved':
        return 'status-reserved'
      case 'Sold':
        return 'status-sold'
      default:
        return 'status-available'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Available':
        return '✅'
      case 'Reserved':
        return '⏳'
      case 'Sold':
        return '❤️'
      default:
        return '✅'
    }
  }

  return (
    <motion.div
      className={`puppy-card ${puppy.status.toLowerCase()}`}
      variants={cardVariants}
      whileHover={{ 
        y: -8, 
        transition: { duration: 0.3, ease: "easeOut" }
      }}
      onClick={onClick}
    >
      {/* Image Container */}
      <div className="puppy-image-container">
        <div
          className="puppy-image-bg"
          style={{ backgroundImage: `url(${puppy.image})` }}
          role="img"
          aria-label={`${puppy.name} - ${puppy.breed} puppy`}
        />
        
        {/* Status Badge */}
        <motion.div 
          className={`status-badge ${getStatusColor(puppy.status)}`}
          initial={{ scale: 0, rotate: 180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: index * 0.1 + 0.5, type: "spring", stiffness: 300 }}
        >
          <span className="status-icon">{getStatusIcon(puppy.status)}</span>
          <span className="status-text">{puppy.status}</span>
        </motion.div>

        {/* Sold Overlay for sold puppies */}
        {puppy.status === 'Sold' && (
          <div className="sold-overlay">
            <div className="sold-content">
              <span className="sold-text">Sold</span>
              <span className="sold-date">
                {puppy.soldDate && new Date(puppy.soldDate).toLocaleDateString()}
              </span>
              {puppy.newFamily && (
                <span className="new-family">to {puppy.newFamily}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="puppy-info">
        <div className="puppy-header">
          <h3 className="puppy-name">{puppy.name}</h3>
          <span className="puppy-price">{puppy.price}</span>
        </div>

        <div className="puppy-details">
          <div className="detail-row">
            <span className="detail-label">Gender:</span>
            <span className="detail-value">{puppy.gender}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Age:</span>
            <span className="detail-value">{puppy.age}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Registration:</span>
            <span className="detail-value">{puppy.registration}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Breed:</span>
            <span className="detail-value">{puppy.breed}</span>
          </div>
          {/* Weight removed as per request */}
        </div>

        <p className="puppy-description">
          {puppy.description.length > 120 
            ? `${puppy.description.substring(0, 120)}...` 
            : puppy.description
          }
        </p>

        {/* Temperament Tags */}
        {puppy.temperament && (
          <div className="temperament-tags">
            {puppy.temperament.split(', ').map((trait, i) => (
              <motion.span
                key={i}
                className="temperament-tag"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 + 0.7 + i * 0.1 }}
              >
                {trait}
              </motion.span>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="card-actions">
          <motion.button
            className="btn btn-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation()
              onClick()
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Learn More
          </motion.button>
          
          {puppy.status === 'Available' && (
            <motion.button
              className="btn btn-primary btn-sm"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/puppy/${puppy.id}`)
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Inquire Now
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default PuppyCard